import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@aitisal/ui/retail-storefront/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@aitisal/ui/retail-storefront/dialog";
import { Button } from "@aitisal/ui/retail-storefront/button";
import { Item } from "@aitisal/contracts/restaurant/menu";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getTitle } from "../../helper/helper";
import { useActiveMenu } from "../../hooks/useActiveMenu";

interface SearchDialogProps {
  className?: string;
}

const SearchDialog = ({ className = "" }: SearchDialogProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { sections } = useActiveMenu();

  const allItems = useMemo(
    () =>
      Array.from(
        new Map(
          (sections.flatMap((s) => s.items || []) || [])
            .map((item: Item) => [item.itemId, item]),
        ).values(),
      ) as Item[],
    [sections],
  );

  const filteredSuggestions = useMemo(() => {
    if (searchTerm.trim() === "") return [];
    return allItems
      .filter(
        (item) =>
          getTitle(item.name)
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) && item.isSeparatelySellable,
      )
      .slice(0, 8);
  }, [allItems, searchTerm]);

  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  const goToResults = () => {
    if (!searchTerm.trim()) return;
    setOpen(false);
    navigate({
      to: "/c/shop",
      search: { category: undefined, search: searchTerm.trim() },
    });
  };

  const handleSelect = (item: Item) => {
    setOpen(false);
    navigate({
      to: "/c/product/$itemId",
      params: { itemId: item.itemId },
      resetScroll: false,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Search products"
          className={`p-2 h-auto w-auto rounded-full hover:bg-gray-100 transition-colors cursor-pointer ${className}`}
        >
          <Search className="size-5 text-gray-700" />
        </Button>
      </DialogTrigger>
      <DialogContent className="top-[12%] translate-y-0 w-[calc(100vw-2rem)] sm:w-full max-w-lg gap-0 p-0 rounded-2xl overflow-hidden border-gray-200 shadow-2xl">
        <DialogTitle className="sr-only">Search products</DialogTitle>
        <DialogDescription className="sr-only">
          Type to search the catalogue, press enter to see all matching
          products.
        </DialogDescription>
        <Command shouldFilter={false} className="w-full">
          <div className="flex items-center gap-2.5 border-b border-gray-100 px-4">
            <Search className="size-4 text-gray-400 shrink-0" />
            <CommandInput
              value={searchTerm}
              onValueChange={setSearchTerm}
              placeholder="Search products here"
              className="h-12 border-0 px-0 text-sm focus:ring-0"
            />
          </div>
          <CommandList className="max-h-[50vh] py-1">
            <CommandEmpty className="py-8 text-center text-sm text-gray-500">
              No products found for "{searchTerm}"
            </CommandEmpty>
            {searchTerm.trim() !== "" && (
              <CommandGroup>
                <CommandItem
                  value="see-all-results"
                  onSelect={goToResults}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer text-sm font-medium text-[var(--brand-color)]"
                >
                  <Search className="size-4 shrink-0" />
                  <span className="flex-1 truncate">
                    See all results for "{searchTerm}"
                  </span>
                  <ArrowRight className="size-4 shrink-0 opacity-60" />
                </CommandItem>
              </CommandGroup>
            )}
            {filteredSuggestions.length > 0 && (
              <CommandGroup heading="Products">
                {filteredSuggestions.map((item) => (
                  <CommandItem
                    key={item.itemId}
                    value={item.itemId}
                    onSelect={() => handleSelect(item)}
                    className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-50 last:border-0 cursor-pointer"
                  >
                    <img
                      src={
                        item.images?.[0]?.thumbnailImage ||
                        "/images/img-placeholder.svg"
                      }
                      alt=""
                      className="w-10 h-10 object-cover rounded-lg shrink-0"
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium text-gray-800">
                        {getTitle(item.name)}
                      </span>
                      <span className="truncate text-xs text-primary font-bold">
                        Rs. {(item.price / 100).toFixed(2)}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
